import axios from "axios";
import { serviceBaseUrl } from "../constants/appConstants";
import { User } from "../types/push";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_PUBLIC_VAPID_KEY;

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  try {
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      return registration;
    } else {
      console.warn("Service workers are not supported in this browser.");
      return null;
    }
  } catch (error) {
    console.error("Failed to register service worker:", error);
    return null;
  }
}

export async function subscribeToPush(registration: ServiceWorkerRegistration): Promise<boolean> {
  try {
    const existingSubscription = await registration.pushManager.getSubscription();

    const subscription =
      existingSubscription ||
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      }));

    console.debug("Attempting to subscribe with:", subscription);

    const response = await axios.post(`${serviceBaseUrl}/subscribe`, subscription, {
      headers: { "Content-Type": "application/json" },
    });

    if (response.data.code === 1100) {
      console.info("Push subscription confirmed by server.");
      return true;
    } else {
      console.warn("Server rejected push subscription:", response.data);
      if (!existingSubscription) {
        await subscription.unsubscribe();
        console.info("Unsubscribed due to failed confirmation.");
      }
      return false;
    }
  } catch (error) {
    console.error("Error during push subscription:", error);
    return false;
  }
}

export async function unsubscribeFromPush(reg: ServiceWorkerRegistration): Promise<boolean> {
  try {
    const subscription = await reg.pushManager.getSubscription();

    if (!subscription) {
      const tempSubscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      
      await axios.post(`${serviceBaseUrl}/unsubscribe`, tempSubscription);
      await tempSubscription.unsubscribe();
      return true;
    }

    const response = await axios.post(`${serviceBaseUrl}/unsubscribe`, subscription);

    if (response.data.code === 1102) {
      const result = await subscription.unsubscribe();
      console.info("Unsubscribed from push notifications:", result);
      return result;
    }

    console.warn("Server failed to remove subscription:", response.data);
    return false;
  } catch (err) {
    console.error("Error during unsubscribe:", err);
    return false;
  }
}

export async function fetchUsers(): Promise<User[] | null> {
  try {
    const response = await axios.get(`${serviceBaseUrl}/users`, {
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });

    if (response.data.code === 2010 && Array.isArray(response.data.result)) {
      return response.data.result;
    } else {
      console.warn("Unexpected response when fetching users:", response.data);
      return null;
    }
  } catch (error) {
    console.error("Error fetching users:", error);
    return null;
  }
}

export async function sendNotification(userId: string, message: string) {
  try {
    const response = await axios.post(
      `${serviceBaseUrl}/sendNotification`,
      {},
      {
        params: { userId, message },
        headers: {
          Accept: "application/json",
        },
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error sending notification:", error);
    throw error;
  }
}



export async function sendNotifications(message: string) {
  try {
    const response = await axios.post(
      `${serviceBaseUrl}/sendNotifications?message=${encodeURIComponent(message)}`,
      {},
      {
        headers: {
          Accept: "application/json",
        },
        withCredentials: true,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error in sendNotifications service:", error);
    throw error;
  }
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const grantUserAccess = async (userId: string): Promise<boolean> => {
  try {
    await axios.post(`${serviceBaseUrl}/userGrant`, null, {
      params: { userId },
      withCredentials: true,
    });
    return true;
  } catch (error) {
    console.error("Grant access failed:", error);
    return false;
  }
};

export const denyUserAccess = async (userId: string): Promise<boolean> => {
  try {
    await axios.post(`${serviceBaseUrl}/userDenied`, null, {
      params: { userId },
      withCredentials: true,
    });
    return true;
  } catch (error) {
    console.error("Deny access failed:", error);
    return false;
  }
};